// apps/web/src/pages/monitoramento/monitor-page.tsx — Fase 03, Plano 06 (MON-01, MON-03).
import React, { useEffect, useState } from 'react';
import {
  Tv,
  Radio,
  WifiOff,
  Loader2,
  Maximize,
  Clock,
  User,
  Layers,
  PlusCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import type { MonitorEventPayload, VisaoGeralItem } from '@pieltec/contracts';
import { RegistroConsumoProgressoModal } from './registro-form';

function ehVisaoGeralItem(valor: unknown): valor is VisaoGeralItem {
  if (typeof valor !== 'object' || valor === null) return false;
  const item = valor as Record<string, unknown>;
  return (
    typeof item.opId === 'string' &&
    typeof item.opNumero === 'string' &&
    typeof item.clienteNome === 'string' &&
    typeof item.materialNome === 'string' &&
    typeof item.loteNumero === 'string' &&
    typeof item.dataCriacao === 'string' &&
    typeof item.previsaoTermino === 'string' &&
    typeof item.status === 'string' &&
    typeof item.progressoPct === 'number' &&
    typeof item.desvioConsumo === 'string'
  );
}

function extrairItens(valor: unknown): VisaoGeralItem[] {
  if (typeof valor !== 'object' || valor === null) return [];
  const resposta = valor as Record<string, unknown>;
  return Array.isArray(resposta.data) ? resposta.data.filter(ehVisaoGeralItem) : [];
}

function ehEventoMonitoramento(valor: unknown): valor is MonitorEventPayload {
  if (typeof valor !== 'object' || valor === null) return false;
  const evento = valor as Record<string, unknown>;
  return typeof evento.opId === 'string' && typeof evento.timestamp === 'string';
}

export const MonitorRealtimePage: React.FC = () => {
  const [itens, setItens] = useState<VisaoGeralItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [conexaoStatus, setConexaoStatus] = useState<'conectado' | 'reconectando' | 'desconectado'>('desconectado');
  const [modalRegistroOs, setModalRegistroOs] = useState<{ osId: string; opNumero?: string; celulaNome?: string } | null>(null);
  const [modoTv, setModoTv] = useState(false);

  const carregarDadosIniciais = async () => {
    try {
      const res = await fetch('/api/v1/monitoramento/visao-geral?limit=50&status=EM_ANDAMENTO');
      if (res.ok) {
        const json: unknown = await res.json();
        setItens(extrairItens(json));
      }
    } catch {
      // Mantém dados anteriores
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarDadosIniciais();

    // Inicia conexão SSE via EventSource nativo
    let eventSource: EventSource | null = null;
    try {
      eventSource = new EventSource('/api/v1/monitoramento/stream');

      eventSource.onopen = () => {
        setConexaoStatus('conectado');
      };

      eventSource.onmessage = (event) => {
        try {
          const dados: unknown = event.data;
          if (typeof dados !== 'string') return;
          const bruto: unknown = JSON.parse(dados);
          if (!ehEventoMonitoramento(bruto)) return;
          const payload = bruto;
          // Atualiza lista em tempo real
          setItens((prev) => {
            return prev.map((item) => {
              if (item.opId === payload.opId) {
                return {
                  ...item,
                  progressoPct: payload.progressoPct ?? item.progressoPct,
                  operadorAtualNome: payload.operadorNome ?? item.operadorAtualNome,
                };
              }
              return item;
            });
          });
        } catch {
          // Ignora parsing de evento não-json
        }
      };

      eventSource.onerror = () => {
        setConexaoStatus('reconectando');
      };
    } catch {
      setConexaoStatus('desconectado');
    }

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  return (
    <div className={`flex flex-col h-full space-y-4 p-4 max-w-7xl mx-auto ${modoTv ? 'bg-slate-950 text-white min-h-screen' : ''}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Tv className="w-6 h-6 text-slate-800 dark:text-white" />
            Monitor de Produção em Tempo Real
          </h1>
          <p className="text-sm text-slate-500">
            Acompanhe a produção ao vivo e a conexão com o chão de fábrica.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Indicador de Conexão SSE */}
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border">
            {conexaoStatus === 'conectado' && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Radio className="w-3.5 h-3.5 animate-pulse" />
                Conectado ao vivo
              </span>
            )}
            {conexaoStatus === 'reconectando' && (
              <span className="flex items-center gap-1 text-amber-600">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Reconectando...
              </span>
            )}
            {conexaoStatus === 'desconectado' && (
              <span className="flex items-center gap-1 text-rose-600">
                <WifiOff className="w-3.5 h-3.5" />
                Desconectado
              </span>
            )}
          </div>

          <button
            onClick={() => setModoTv(!modoTv)}
            className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-xs"
          >
            <Maximize className="w-3.5 h-3.5" />
            {modoTv ? 'Sair do Modo TV' : 'Modo TV Galpão'}
          </button>
        </div>
      </div>

      {/* Grid de Cartões de OPs em Tempo Real */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : itens.length === 0 ? (
        <div className="p-12 text-center text-slate-400 text-xs border rounded-xl bg-white">
          Nenhuma OP em execução no momento.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {itens.map((item) => (
            <div
              key={item.opId}
              className="p-5 rounded-xl border bg-white shadow-xs space-y-3 relative overflow-hidden"
            >
              {/* Barra de Progresso no Topo */}
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-slate-900 h-full transition-all duration-300"
                  style={{ width: `${item.progressoPct}%` }}
                />
              </div>

              {/* Título da OP */}
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-mono font-semibold text-slate-400">
                    {item.opNumero}
                  </span>
                  <h3 className="text-sm font-bold text-slate-900 truncate max-w-[200px]">
                    {item.materialNome}
                  </h3>
                  <p className="text-xs text-slate-500 truncate max-w-[200px]">
                    {item.clienteNome}
                  </p>
                </div>

                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                  {item.progressoPct}% concluído
                </span>
              </div>

              {/* Detalhes de Chão de Fábrica */}
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pt-2 border-t">
                <div className="flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{item.celulaAtualNome ?? 'Célula Ativa'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{item.operadorAtualNome ?? 'Sem operador'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span>Prev: {item.previsaoTermino}</span>
                </div>
                <div>
                  {item.desvioConsumo === 'acima' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold">
                      <TrendingUp className="w-3 h-3" /> Consumo Acima
                    </span>
                  )}
                  {item.desvioConsumo === 'abaixo' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 font-semibold">
                      <TrendingDown className="w-3 h-3" /> Consumo Abaixo
                    </span>
                  )}
                  {item.desvioConsumo === 'dentro' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                      <Minus className="w-3 h-3" /> No Planejado
                    </span>
                  )}
                </div>
              </div>

              {/* Ação Rápida de Apontamento / Registro */}
              <div className="pt-2">
                <button
                  disabled={!item.osId}
                  title={!item.osId ? 'Nenhuma Ordem de Serviço ativa para esta OP' : undefined}
                  onClick={() => {
                    if (!item.osId) return;
                    setModalRegistroOs({
                      osId: item.osId,
                      opNumero: item.opNumero,
                      celulaNome: item.celulaAtualNome,
                    });
                  }}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-md text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  Registrar Consumo / Progresso
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Registro de Consumo/Progresso */}
      {modalRegistroOs && (
        <RegistroConsumoProgressoModal
          osId={modalRegistroOs.osId}
          opNumero={modalRegistroOs.opNumero}
          celulaNome={modalRegistroOs.celulaNome}
          onClose={() => setModalRegistroOs(null)}
          onSuccess={() => {
            void carregarDadosIniciais();
          }}
        />
      )}
    </div>
  );
};
